import { type FormEvent, type ReactNode, useState } from "react";
import { useCreateUser, useUpdateUser, useUsers } from "../api/hooks/useUsers";
import type { UserRole } from "../api/types";
import { ApiError } from "../api/client";
import { formatDate } from "../utils/format";

/**
 * Admin-only Users page — not part of the prototype (plan adds it on top).
 * Reuses the Trades-page table pattern (surface card, sticky header row,
 * bordered rows) and the Control-page toggle-switch visual for is_active,
 * rather than introducing new table chrome.
 */
// Last column needs to fit "Деактивировать" (the longest of the two toggle
// labels) without wrapping or spilling into the row border — 90px clipped
// it. Shared between the header and data rows so they can't drift apart.
const GRID_COLS = "1fr 200px 100px 120px 84px 138px";

export function Users() {
  const { data: users, isLoading } = useUsers();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("operator");
  const [error, setError] = useState<string | null>(null);

  const handleCreate = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    createUser.mutate(
      { email, display_name: displayName, password, role },
      {
        onSuccess: () => {
          setShowForm(false);
          setEmail("");
          setDisplayName("");
          setPassword("");
          setRole("operator");
        },
        onError: (err) => setError(err instanceof ApiError ? err.detail : "Не удалось создать пользователя"),
      },
    );
  };

  return (
    <div style={{ padding: "26px 28px 44px", display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          style={{
            padding: "8px 16px",
            border: 0,
            borderRadius: 10,
            background: "var(--accent)",
            color: "var(--onfill)",
            font: "600 12.5px 'Golos Text',sans-serif",
            cursor: "pointer",
          }}
        >
          {showForm ? "Отмена" : "Добавить пользователя"}
        </button>
      </div>

      {showForm && (
        <section style={{ borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", padding: 18 }}>
          <form onSubmit={handleCreate} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
            <Field label="Email">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Имя">
              <input type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Пароль">
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="Роль">
              <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} style={inputStyle}>
                <option value="operator">operator</option>
                <option value="admin">admin</option>
              </select>
            </Field>
            <button
              type="submit"
              disabled={createUser.isPending}
              style={{
                padding: "9px 16px",
                border: 0,
                borderRadius: 10,
                background: "var(--accent)",
                color: "var(--onfill)",
                font: "600 12.5px 'Golos Text',sans-serif",
                cursor: createUser.isPending ? "default" : "pointer",
                opacity: createUser.isPending ? 0.7 : 1,
              }}
            >
              Создать
            </button>
          </form>
          {error && (
            <div style={{ marginTop: 10, padding: "9px 12px", borderRadius: 10, background: "var(--shortsoft)", color: "var(--short)", font: "500 12.5px 'Golos Text',sans-serif" }}>
              {error}
            </div>
          )}
        </section>
      )}

      <section style={{ borderRadius: 16, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: GRID_COLS,
            gap: 12,
            padding: "10px 16px",
            borderBottom: "1px solid var(--line)",
            background: "var(--surface3)",
            font: "600 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "var(--fg2)",
          }}
        >
          <span>Email</span>
          <span>Имя</span>
          <span>Роль</span>
          <span>Регистрация</span>
          <span>Активен</span>
          <span />
        </div>
        {isLoading && <div style={{ padding: 16, font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Загрузка…</div>}
        {(users ?? []).map((u) => (
          <div
            key={u.id}
            style={{
              display: "grid",
              gridTemplateColumns: GRID_COLS,
              gap: 12,
              alignItems: "center",
              padding: "var(--rowpad) 16px",
              borderBottom: "1px solid var(--line)",
              font: "400 12.5px 'JetBrains Mono',monospace",
            }}
          >
            <span>{u.email}</span>
            <span style={{ font: "400 12.5px 'Golos Text',sans-serif" }}>{u.display_name}</span>
            <span
              style={{
                font: "600 10.5px 'Golos Text',sans-serif",
                letterSpacing: ".04em",
                padding: "2px 7px",
                borderRadius: 5,
                background: u.role === "admin" ? "var(--accentsoft)" : "var(--surface2)",
                color: u.role === "admin" ? "var(--accent)" : "var(--fg2)",
                justifySelf: "start",
              }}
            >
              {u.role}
            </span>
            <span style={{ color: "var(--fg2)" }}>{formatDate(u.created_at)}</span>
            <span style={{ color: u.is_active ? "var(--long)" : "var(--fg3)" }}>{u.is_active ? "да" : "нет"}</span>
            <button
              type="button"
              onClick={() => updateUser.mutate({ id: u.id, is_active: !u.is_active })}
              disabled={updateUser.isPending}
              style={{
                padding: "5px 10px",
                border: "1px solid var(--line2)",
                borderRadius: 8,
                background: "transparent",
                color: "var(--fg2)",
                cursor: updateUser.isPending ? "default" : "pointer",
                font: "600 11px 'Golos Text',sans-serif",
                whiteSpace: "nowrap",
                justifySelf: "start",
              }}
            >
              {u.is_active ? "Деактивировать" : "Активировать"}
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ font: "500 11.5px 'Golos Text',sans-serif", color: "var(--fg2)" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  padding: "8px 11px",
  border: "1px solid var(--line)",
  borderRadius: 9,
  background: "var(--surface)",
  color: "var(--fg)",
  font: "400 12.5px 'Golos Text',sans-serif",
  outline: "none",
} as const;
